import ExpandMore from "@mui/icons-material/ExpandMore";
import { Box, Button, Stack, Switch, ToggleButton, ToggleButtonGroup, Typography, useTheme } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FeatureItem } from "../feature.types";

type Props = {
    feature: FeatureItem;
    index: number;
};
export const FeatureSubmodule = (props: Props) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const { feature, index } = props;

    const [diagnosticExpanded, setDiagnosticExpanded] = useState(true);

    return (
        <Box key={index}>
            {/* Feature - aligned under entity icon */}
            <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{
                    padding: "0px", // Remove extra padding to align with entity
                }}
            >
                {/* Chevron for expandable features OR empty space for alignment */}
                {feature.expandable ?
                    <Box
                        onClick={() => setDiagnosticExpanded(!diagnosticExpanded)}
                        sx={{
                            width: "24px",
                            height: "24px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            marginLeft: "0px", // Align under entity icon
                            marginRight: "16px",
                            transform: diagnosticExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                            transition: "transform 0.2s ease",
                        }}
                    >
                        <ExpandMore sx={{ fontSize: "1.25rem", color: "#393A36" }} />
                    </Box>
                :   <Box
                        sx={{
                            width: "24px",
                            height: "24px",
                            marginLeft: "0px",
                            marginRight: "16px",
                        }}
                    />
                }

                {/* Feature content */}
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "4px" }}>
                    <Typography
                        variant="body"
                        sx={{
                            color: "#393A36",
                            marginBottom: "4px",
                        }}
                    >
                        {feature.name}
                    </Typography>
                    <Typography
                        variant="small"
                        sx={{
                            color: "#767673",
                        }}
                    >
                        {feature.description}
                    </Typography>
                </Box>

                {/* Feature controls */}
                <Stack direction="row" alignItems="center" spacing={2}>
                    {feature.configureButton && (
                        <Button
                            variant="outlined"
                            size="small"
                            disabled={!feature.enabled}
                            onClick={feature.configureButton.onClick}
                            sx={{
                                fontWeight: 700,
                                textTransform: "none",
                                borderRadius: "44px",
                                padding: "0px 20px",
                                height: "40px",
                                borderColor: theme.palette.primary[600],
                                color: theme.palette.primary[600],
                                "&:hover": {
                                    borderColor: theme.palette.primary[700],
                                    backgroundColor: theme.palette.primary[50],
                                },
                                "&:disabled": {
                                    opacity: 0.36,
                                    borderColor: theme.custom.grey[500],
                                    color: theme.custom.grey[2800],
                                },
                            }}
                        >
                            {feature.configureButton.text}
                        </Button>
                    )}
                    {feature.onToggle && feature.control === "v1v2" && (
                        <ToggleButtonGroup
                            value={feature.enabled ? "v2" : "v1"}
                            exclusive
                            size="small"
                            onChange={(_, nextValue: "v1" | "v2" | null) => {
                                if (nextValue === null) return;
                                feature.onToggle?.(nextValue === "v2");
                            }}
                            sx={{
                                "& .MuiToggleButtonGroup-grouped": {
                                    border: `1px solid ${theme.palette.primary[600]}`,
                                    color: theme.palette.primary[600],
                                    fontWeight: 700,
                                    textTransform: "none",
                                    minWidth: "48px",
                                    height: "32px",
                                    paddingInline: "16px",
                                    "&.Mui-selected": {
                                        backgroundColor: theme.palette.primary[600],
                                        color: "#fff",
                                        "&:hover": {
                                            backgroundColor: theme.palette.primary[700],
                                        },
                                    },
                                    "&:hover": {
                                        backgroundColor: theme.palette.primary[50],
                                    },
                                },
                            }}
                            aria-label={t("feature_switch.ux_version.aria", "Interface version")}
                        >
                            <ToggleButton value="v1" aria-label="v1">
                                {"v1"}
                            </ToggleButton>
                            <ToggleButton value="v2" aria-label="v2">
                                {"v2"}
                            </ToggleButton>
                        </ToggleButtonGroup>
                    )}
                    {feature.onToggle && feature.control !== "v1v2" && (
                        <Switch
                            checked={feature.enabled}
                            onChange={(e) => feature.onToggle?.(e.target.checked)}
                            sx={{
                                width: 44,
                                height: 24,
                                padding: 0,
                                "& .MuiSwitch-switchBase": {
                                    padding: 0,
                                    margin: "4px",
                                    transitionDuration: "300ms",
                                    "&.Mui-checked": {
                                        transform: "translateX(20px)",
                                        color: "#fff",
                                        "& + .MuiSwitch-track": {
                                            backgroundColor: theme.palette.primary[600],
                                            opacity: 1,
                                            border: 0,
                                        },
                                    },
                                },
                                "& .MuiSwitch-thumb": {
                                    boxSizing: "border-box",
                                    width: 16,
                                    height: 16,
                                },
                                "& .MuiSwitch-track": {
                                    borderRadius: 20,
                                    backgroundColor: "#D8D7D5",
                                    opacity: 1,
                                    transition: "background-color 0.5s",
                                },
                            }}
                        />
                    )}
                </Stack>
            </Stack>

            {/* Sub-features (shown when feature is expandable and expanded) */}
            {feature.hasSubFeatures && feature.subFeatures && feature.expandable && diagnosticExpanded && (
                <Box sx={{ marginTop: "16px" }}>
                    {feature.subFeatures.map((subFeature, subIndex) => (
                        <Stack
                            key={subIndex}
                            direction="row"
                            alignItems="center"
                            spacing={2}
                            sx={{
                                padding: "8px 0px 8px 64px", // Indent sub-features more to the right
                            }}
                        >
                            {/* Sub-feature content */}
                            <Box
                                sx={{
                                    flex: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "4px",
                                }}
                            >
                                <Typography
                                    variant="body"
                                    sx={{
                                        color: "#393A36",
                                        marginBottom: "4px",
                                    }}
                                >
                                    {subFeature.name}
                                </Typography>
                                <Typography
                                    variant="small"
                                    sx={{
                                        color: "#767673",
                                    }}
                                >
                                    {subFeature.description}
                                </Typography>
                            </Box>

                            {/* Sub-feature controls */}
                            <Stack direction="row" alignItems="center" spacing={2}>
                                {subFeature.buttonText && (
                                    <Button
                                        variant="outlined"
                                        sx={{
                                            fontWeight: 700,
                                            textTransform: "none",
                                            borderRadius: "44px",
                                            padding: "0px 20px",
                                            height: "40px",
                                            borderColor: theme.palette.primary[600],
                                            color: theme.palette.primary[600],
                                            backgroundColor: "white",
                                            "&:hover": {
                                                backgroundColor: theme.palette.primary[50],
                                                borderColor: theme.palette.primary[700],
                                            },
                                        }}
                                    >
                                        {subFeature.buttonText}
                                    </Button>
                                )}
                                <Switch
                                    checked={subFeature.enabled}
                                    onChange={(e) => subFeature.onToggle?.(e.target.checked)}
                                    sx={{
                                        width: 44,
                                        height: 24,
                                        padding: 0,
                                        "& .MuiSwitch-switchBase": {
                                            padding: 0,
                                            margin: "4px",
                                            transitionDuration: "300ms",
                                            "&.Mui-checked": {
                                                transform: "translateX(20px)",
                                                color: "#fff",
                                                "& + .MuiSwitch-track": {
                                                    backgroundColor: theme.palette.primary[600],
                                                    opacity: 1,
                                                    border: 0,
                                                },
                                            },
                                        },
                                        "& .MuiSwitch-thumb": {
                                            boxSizing: "border-box",
                                            width: 16,
                                            height: 16,
                                        },
                                        "& .MuiSwitch-track": {
                                            borderRadius: 20,
                                            backgroundColor: "#D8D7D5",
                                            opacity: 1,
                                            transition: "background-color 0.5s",
                                        },
                                    }}
                                />
                            </Stack>
                        </Stack>
                    ))}
                </Box>
            )}
        </Box>
    );
};
